import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as d3 from "d3";
import { Box, Paper, Tooltip, Typography } from "@mui/material";
import type {
  CapabilityMapDataset,
  CapabilityMapFilters,
  DomainMode,
  MarketStatus,
  NodeKind,
} from "./types";

interface Props {
  dataset: CapabilityMapDataset;
  filters: CapabilityMapFilters;
  domainMode: DomainMode;
  marketStatusFilter: MarketStatus | null;
  searchQuery: string;
  highlightedNodeIds: Set<string>;
  onNodeClick: (nodeId: string, kind: NodeKind) => void;
}

interface SunburstNode {
  id: string;
  name: string;
  kind: NodeKind | "root";
  marketStatus?: MarketStatus;
  companyCount?: number;
  demandCount?: number;
  children?: SunburstNode[];
  value?: number;
}

interface TooltipState {
  x: number;
  y: number;
  label: string;
  companyCount?: number;
  demandCount?: number;
  marketStatus?: MarketStatus;
}

// Colors per level / kind
const KIND_COLORS: Record<string, string> = {
  root: "#1e1e1e",
  capability_cluster: "#1565C0",
  sub_capability: "#6A1B9A",
  use_case: "#2E7D32",
};

function marketOpacity(status: MarketStatus | undefined): number {
  if (status === "crowded") return 1.0;
  if (status === "moderate") return 0.7;
  return 0.45;
}

function buildHierarchy(
  dataset: CapabilityMapDataset,
  filters: CapabilityMapFilters,
  domainMode: DomainMode,
  marketStatusFilter: MarketStatus | null,
  searchQuery: string,
): SunburstNode {
  const query = searchQuery.toLowerCase();

  // Pre-compute which sub-capabilities are reachable from the selected system types
  const allowedSubCapIds = new Set<string>();
  if (filters.systemTypeIds.length > 0) {
    const allowedSysCapIds = new Set<string>();
    for (const st of dataset.systemTypes) {
      if (filters.systemTypeIds.includes(st.id)) {
        st.systemCapabilityIds.forEach((id) => allowedSysCapIds.add(id));
      }
    }
    for (const cluster of dataset.clusters) {
      for (const sc of cluster.subCapabilities) {
        if (
          (sc.systemCapabilityIds ?? []).some((id) => allowedSysCapIds.has(id))
        ) {
          allowedSubCapIds.add(sc.id);
        }
      }
    }
  }

  // Pre-compute tender count per system capability, restricted to selected source types
  const tendersPerSysCap = new Map<string, number>();
  if (filters.sourceTypes.length > 0) {
    for (const t of dataset.tenders) {
      if (!filters.sourceTypes.includes(t.sourceType)) continue;
      // Each tender applies to its systemTypes' system capabilities
      for (const stId of t.systemTypeIds) {
        const st = dataset.systemTypes.find((s) => s.id === stId);
        if (!st) continue;
        for (const sysCapId of st.systemCapabilityIds) {
          tendersPerSysCap.set(
            sysCapId,
            (tendersPerSysCap.get(sysCapId) ?? 0) + 1,
          );
        }
      }
    }
  }

  const clusters = dataset.clusters
    .map((cluster) => {
      const subCaps = cluster.subCapabilities
        .map((sc) => {
          // System type filter
          if (filters.systemTypeIds.length > 0 && !allowedSubCapIds.has(sc.id))
            return null;

          // Count companies covering this sub-cap (respects domain + company-type filters)
          const companyCount = dataset.companies.filter(
            (c) =>
              c.capabilitiesCovered.includes(sc.id) &&
              (domainMode === "all" ||
                c.domains.includes(domainMode as "defense" | "industrial")) &&
              (filters.companyTypes.length === 0 ||
                filters.companyTypes.some((t) => c.companyTypes.includes(t))),
          ).length;

          // Derive market status from company count
          const ms: MarketStatus =
            companyCount >= 4
              ? "crowded"
              : companyCount >= 2
                ? "moderate"
                : "open";

          if (marketStatusFilter && ms !== marketStatusFilter) return null;

          const useCases = sc.useCases
            .map((uc) => {
              if (
                domainMode !== "all" &&
                !uc.domains.includes(domainMode as "defense" | "industrial")
              )
                return null;
              if (marketStatusFilter && uc.marketStatus !== marketStatusFilter)
                return null;
              if (
                filters.useCaseTags.length > 0 &&
                !filters.useCaseTags.some((t) => uc.useCaseTags.includes(t))
              ) {
                return null;
              }

              const matchesSearch =
                !query ||
                uc.label.toLowerCase().includes(query) ||
                sc.label.toLowerCase().includes(query) ||
                cluster.label.toLowerCase().includes(query);

              return {
                id: uc.id,
                name: uc.label,
                kind: "use_case" as NodeKind,
                marketStatus: uc.marketStatus,
                companyCount: uc.companyIds.length,
                value: Math.max(1, uc.companyIds.length),
                _matchesSearch: matchesSearch,
              };
            })
            .filter(Boolean) as (SunburstNode & { _matchesSearch: boolean })[];

          // If a use-case-tag filter is active, drop sub-caps with no matching use cases
          if (filters.useCaseTags.length > 0 && useCases.length === 0)
            return null;

          const matchesSearch =
            !query ||
            sc.label.toLowerCase().includes(query) ||
            cluster.label.toLowerCase().includes(query) ||
            useCases.some((uc) => uc._matchesSearch);

          // Sum demand counts from linked system capabilities (optionally restricted by source type)
          const scDemand = (sc.systemCapabilityIds ?? []).reduce(
            (sum, scId) => {
              if (filters.sourceTypes.length > 0) {
                return sum + (tendersPerSysCap.get(scId) ?? 0);
              }
              const sysCap = dataset.systemCapabilities?.find(
                (s) => s.id === scId,
              );
              return sum + (sysCap?.demandCount ?? 0);
            },
            0,
          );

          return {
            id: sc.id,
            name: sc.label,
            kind: "sub_capability" as NodeKind,
            marketStatus: ms,
            companyCount,
            demandCount: scDemand,
            children: useCases,
            _matchesSearch: matchesSearch,
          };
        })
        .filter(Boolean) as (SunburstNode & { _matchesSearch: boolean })[];

      if (subCaps.length === 0) return null;

      const clusterCompanyCount = dataset.companies.filter(
        (c) =>
          cluster.subCapabilities.some((sc) =>
            c.capabilitiesCovered.includes(sc.id),
          ) &&
          (domainMode === "all" ||
            c.domains.includes(domainMode as "defense" | "industrial")) &&
          (filters.companyTypes.length === 0 ||
            filters.companyTypes.some((t) => c.companyTypes.includes(t))),
      ).length;

      return {
        id: cluster.id,
        name: cluster.label,
        kind: "capability_cluster" as NodeKind,
        companyCount: clusterCompanyCount,
        children: subCaps,
        _matchesSearch:
          !query ||
          cluster.label.toLowerCase().includes(query) ||
          subCaps.some((sc) => sc._matchesSearch),
      };
    })
    .filter(Boolean) as (SunburstNode & { _matchesSearch: boolean })[];

  return {
    id: "root",
    name: "Capabilities",
    kind: "capability_cluster",
    children: clusters,
  };
}

const CapabilityMapSunburst: React.FC<Props> = ({
  dataset,
  filters,
  domainMode,
  marketStatusFilter,
  searchQuery,
  highlightedNodeIds,
  onNodeClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<string[]>(["All Capabilities"]);
  const currentRootRef =
    useRef<d3.HierarchyRectangularNode<SunburstNode> | null>(null);

  const hierarchyData = useMemo(
    () =>
      buildHierarchy(
        dataset,
        filters,
        domainMode,
        marketStatusFilter,
        searchQuery,
      ),
    [dataset, filters, domainMode, marketStatusFilter, searchQuery],
  );

  const draw = useCallback(() => {
    const container = containerRef.current;
    const svgEl = svgRef.current;
    if (!container || !svgEl) return;

    const width = container.clientWidth || 600;
    const height = container.clientHeight || 600;
    const radius = Math.min(width, height) / 2;

    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();
    svg.attr("viewBox", `${-width / 2} ${-height / 2} ${width} ${height}`);

    const root = d3
      .hierarchy<SunburstNode>(hierarchyData)
      .sum((d) => d.value ?? 1)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

    const partition = d3.partition<SunburstNode>().size([2 * Math.PI, radius]);
    partition(root);

    currentRootRef.current = root as d3.HierarchyRectangularNode<SunburstNode>;

    const arc = d3
      .arc<d3.HierarchyRectangularNode<SunburstNode>>()
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .padAngle((d) => Math.min((d.x1 - d.x0) / 2, 0.005))
      .padRadius(radius / 2)
      .innerRadius((d) => d.y0)
      .outerRadius((d) => Math.max(d.y0, d.y1 - 1));

    const g = svg.append("g");

    const query = searchQuery.toLowerCase();

    const paths = g
      .selectAll<SVGPathElement, d3.HierarchyRectangularNode<SunburstNode>>(
        "path",
      )
      .data(
        (root as d3.HierarchyRectangularNode<SunburstNode>)
          .descendants()
          .filter((d) => d.depth > 0),
      )
      .join("path")
      .attr("d", arc)
      .attr("fill", (d) => KIND_COLORS[d.data.kind] ?? "#424242")
      .attr("fill-opacity", (d) => {
        const ms = d.data.marketStatus;
        const base = marketOpacity(ms);
        // Dim non-highlighted if there's a search or highlight
        if (query && d.data.kind === "use_case") {
          const label = d.data.name.toLowerCase();
          const matches =
            label.includes(query) ||
            (d.parent?.data.name.toLowerCase().includes(query) ?? false) ||
            (d.parent?.parent?.data.name.toLowerCase().includes(query) ??
              false);
          return matches ? base : base * 0.15;
        }
        if (highlightedNodeIds.size > 0 && d.data.kind === "company")
          return 1.0;
        return base;
      })
      .attr("stroke", "#1e1e1e")
      .attr("stroke-width", 0.5)
      .style("cursor", "pointer");

    // Labels for arcs wide enough
    g.selectAll<SVGTextElement, d3.HierarchyRectangularNode<SunburstNode>>(
      "text",
    )
      .data(
        (root as d3.HierarchyRectangularNode<SunburstNode>)
          .descendants()
          .filter((d) => d.depth > 0 && d.depth <= 2 && d.x1 - d.x0 > 0.12),
      )
      .join("text")
      .attr("transform", (d) => {
        const x = (((d.x0 + d.x1) / 2) * 180) / Math.PI;
        const y = (d.y0 + d.y1) / 2;
        return `rotate(${x - 90}) translate(${y},0) rotate(${x < 180 ? 0 : 180})`;
      })
      .attr("dy", "0.35em")
      .attr("text-anchor", "middle")
      .attr("fill", "#fff")
      .attr("font-size", (d) => (d.depth === 1 ? "11px" : "9px"))
      .attr("pointer-events", "none")
      .text((d) => {
        const label = d.data.name;
        const arcLen = ((d.x1 - d.x0) * (d.y0 + d.y1)) / 2;
        const maxChars = Math.floor(arcLen / 7);
        return label.length > maxChars
          ? label.slice(0, maxChars - 1) + "…"
          : label;
      });

    // Centre label
    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "-0.3em")
      .attr("fill", "#aaa")
      .attr("font-size", "12px")
      .text("Click to explore");
    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "1em")
      .attr("fill", "#666")
      .attr("font-size", "10px")
      .text("center = zoom out");

    // Zoom into a node
    function zoomTo(node: d3.HierarchyRectangularNode<SunburstNode>) {
      const x0 = node.x0;
      const x1 = node.x1;
      const y0 = node.y0;

      // Rescale angles to fill the circle
      const xScale = d3
        .scaleLinear()
        .domain([x0, x1])
        .range([0, 2 * Math.PI]);
      const yScale = d3.scaleLinear().domain([y0, radius]).range([0, radius]);

      const newArc = d3
        .arc<d3.HierarchyRectangularNode<SunburstNode>>()
        .startAngle((d) => Math.max(0, Math.min(2 * Math.PI, xScale(d.x0))))
        .endAngle((d) => Math.max(0, Math.min(2 * Math.PI, xScale(d.x1))))
        .padAngle((d) => Math.min((d.x1 - d.x0) / 2, 0.005))
        .padRadius(radius / 2)
        .innerRadius((d) => Math.max(0, yScale(d.y0)))
        .outerRadius((d) => Math.max(0, yScale(d.y1) - 1));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (paths as any)
        .transition()
        .duration(600)
        .ease(d3.easeCubicInOut)
        .attrTween(
          "d",
          (d: d3.HierarchyRectangularNode<SunburstNode>) => () =>
            newArc(d) ?? "",
        );
    }

    // Click handler
    paths.on("click", (event: MouseEvent, d) => {
      event.stopPropagation();

      if (d.data.kind !== "use_case" && d.children) {
        zoomTo(d);
        const path: string[] = [];
        let cur: d3.HierarchyRectangularNode<SunburstNode> | null = d;
        while (cur && cur.depth > 0) {
          path.unshift(cur.data.name);
          cur = cur.parent as d3.HierarchyRectangularNode<SunburstNode> | null;
        }
        setBreadcrumb(["All Capabilities", ...path]);
      }

      if (d.data.kind !== "root") {
        onNodeClick(d.data.id, d.data.kind);
      }
    });

    // Zoom out on centre click
    svg.on("click", () => {
      if (currentRootRef.current) {
        zoomTo(currentRootRef.current);
        setBreadcrumb(["All Capabilities"]);
      }
    });

    // Tooltip
    paths
      .on("mouseenter", (event: MouseEvent, d) => {
        const rect = (event.target as SVGPathElement).getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        setTooltip({
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top,
          label: d.data.name,
          companyCount: d.data.companyCount,
          demandCount: d.data.demandCount,
          marketStatus: d.data.marketStatus,
        });
      })
      .on("mouseleave", () => setTooltip(null));
  }, [hierarchyData, highlightedNodeIds, searchQuery, onNodeClick]);

  useEffect(() => {
    draw();
  }, [draw]);

  // Redraw on resize
  useEffect(() => {
    const ro = new ResizeObserver(() => draw());
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [draw]);

  return (
    <Box
      ref={containerRef}
      sx={{ width: "100%", height: "100%", position: "relative" }}
    >
      <svg ref={svgRef} style={{ width: "100%", height: "100%" }} />

      {/* Breadcrumb */}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          left: 16,
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {breadcrumb.map((crumb, i) => (
          <React.Fragment key={i}>
            {i > 0 && (
              <Typography variant="caption" sx={{ color: "text.disabled" }}>
                /
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{
                color:
                  i === breadcrumb.length - 1
                    ? "primary.main"
                    : "text.secondary",
                fontWeight: i === breadcrumb.length - 1 ? 600 : 400,
              }}
            >
              {crumb}
            </Typography>
          </React.Fragment>
        ))}
      </Box>

      {/* Tooltip */}
      {tooltip && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y - 8,
            transform: "translate(-50%, -100%)",
            p: 1,
            pointerEvents: "none",
            maxWidth: 220,
            zIndex: 10,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {tooltip.label}
          </Typography>
          {tooltip.companyCount !== undefined && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              {tooltip.companyCount}{" "}
              {tooltip.companyCount === 1 ? "company" : "companies"}
            </Typography>
          )}
          {tooltip.demandCount !== undefined && tooltip.demandCount > 0 && (
            <Typography
              variant="caption"
              sx={{ color: "warning.main", display: "block" }}
            >
              {tooltip.demandCount} tender{tooltip.demandCount !== 1 ? "s" : ""}
            </Typography>
          )}
          {tooltip.marketStatus && (
            <Typography
              variant="caption"
              sx={{
                color:
                  tooltip.marketStatus === "crowded"
                    ? "error.main"
                    : tooltip.marketStatus === "moderate"
                      ? "warning.main"
                      : "success.main",
                textTransform: "uppercase",
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              {tooltip.marketStatus}
            </Typography>
          )}
        </Paper>
      )}

      {/* Legend */}
      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          left: 16,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {[
          {
            color: KIND_COLORS.capability_cluster,
            label: "Capability Cluster",
          },
          { color: KIND_COLORS.sub_capability, label: "Sub-Capability" },
          { color: KIND_COLORS.use_case, label: "Use Case" },
        ].map(({ color, label }) => (
          <Box
            key={label}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "2px",
                bgcolor: color,
              }}
            />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {label}
            </Typography>
          </Box>
        ))}
        <Box
          sx={{
            mt: 0.5,
            borderTop: "1px solid",
            borderColor: "divider",
            pt: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "text.disabled", display: "block" }}
          >
            Opacity = market density
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 0.25 }}>
            {(["crowded", "moderate", "open"] as MarketStatus[]).map((s) => (
              <Box
                key={s}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: "#1565C0",
                    opacity: marketOpacity(s),
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "text.disabled", fontSize: "0.6rem" }}
                >
                  {s}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CapabilityMapSunburst;
